import { useEffect, useState } from "react";

/** localStorage key - read on mount, written on every toggle. The
 *  cashier's preference survives reload + scope switch. Stored as
 *  ``"true"`` / ``"false"`` so absence reads as the default
 *  (expanded). */
const STORAGE_KEY = "tilt-pos.sidebarCollapsed";

/** Persisted toggle for the lg+ sidebar's collapsed state. Mobile
 *  drawer is a separate flag - the collapse only applies when the
 *  sidebar is visible (lg+). The hook reads localStorage on mount
 *  and writes back on every change so the choice survives reload.
 *
 *  Returns ``[collapsed, toggle, setCollapsed]`` so callers can wire
 *  a button to toggle, or programmatically force a state on a
 *  layout-event handler if needed.
 */
export function useSidebarCollapse(): readonly [boolean, () => void, (next: boolean) => void] {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Persist on change. Wrapped in try/catch because localStorage can
  // throw in private-browsing modes; fall back to in-memory state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? "true" : "false");
    } catch {
      /* swallow - in-memory state still works */
    }
  }, [collapsed]);

  // Keyboard shortcut - `[` toggles. Skipped while focus is in a text
  // input / textarea / contentEditable so cashiers typing reference
  // numbers don't accidentally collapse the rail.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    function onKey(ev: KeyboardEvent): void {
      if (ev.key !== "[") return;
      const target = ev.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || target.isContentEditable) return;
      }
      setCollapsedState((v) => !v);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggle = (): void => setCollapsedState((v) => !v);
  const setCollapsed = (next: boolean): void => setCollapsedState(next);
  return [collapsed, toggle, setCollapsed] as const;
}
